import * as Sentry from '@sentry/react'

/**
 * Log levels for structured logging
 */
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warning',
  ERROR = 'error',
}

/**
 * Context object for additional log data
 */
interface LogContext {
  [key: string]: any
}

/**
 * Centralized logger that integrates with Sentry
 *
 * Environment behavior:
 * - Local development: logs to console for debugging
 * - Staging & Production: sends logs to Sentry as breadcrumbs and messages
 */
class Logger {
  private isDevelopment = import.meta.env.MODE === 'development'

  /**
   * Internal method to handle logging based on environment
   */
  private log(level: LogLevel, message: string, context?: LogContext) {
    // Always log to console in development for debugging
    if (this.isDevelopment) {
      const consoleMethod =
        level === LogLevel.ERROR
          ? 'error'
          : level === LogLevel.WARN
            ? 'warn'
            : level === LogLevel.DEBUG
              ? 'debug'
              : 'log'
      // eslint-disable-next-line no-console
      console[consoleMethod](
        `[${level.toUpperCase()}] ${message}`,
        context || ''
      )
      return
    }

    Sentry.addBreadcrumb({
      message,
      level: level as any,
      data: context,
      timestamp: Date.now() / 1000,
    })

    if (level === LogLevel.ERROR) {
      Sentry.captureEvent({
        level,
        message,
        extra: context,
        logger: 'frontend',
        timestamp: Date.now() / 1000,
      })
    }
  }

  /**
   * Log debug information
   * Use for detailed debugging information that is normally not needed
   */
  debug(message: string, context?: LogContext) {
    this.log(LogLevel.DEBUG, message, context)
  }

  /**
   * Log informational messages
   * Use for general information about application flow
   */
  info(message: string, context?: LogContext) {
    this.log(LogLevel.INFO, message, context)
  }

  /**
   * Log warnings
   * Use for potentially harmful situations or deprecated functionality
   */
  warn(message: string, context?: LogContext) {
    this.log(LogLevel.WARN, message, context)
  }

  /**
   * Log errors
   * Use for error events that might still allow the application to continue
   */
  error(message: string, context?: LogContext) {
    this.log(LogLevel.ERROR, message, context)
  }

  /**
   * Capture exceptions with context
   * Use for actual exceptions/errors that need to be tracked.
   * `tags` become Sentry tags (searchable and shown in the issue's tag
   * distribution), unlike `context` which lands in non-indexed extra data.
   */
  captureException(
    error: Error,
    context?: LogContext,
    tags?: Record<string, string>
  ) {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console
      console.error('Exception:', error, context || '', tags || '')
    }

    if (!this.isDevelopment) {
      Sentry.captureException(error, { extra: context, tags })
    }
  }
}

export const logger = new Logger()
